import type { BadgeColor, StatusBadgeStatus } from '@clay-run/glaze';

import type { LauncherChannel } from './schemas';

/**
 * V2 coloring module — Glaze-token equivalent of v1's
 * `components/subtype-colors.ts` (that file is UNTOUCHED and stays on its
 * hand-rolled Tailwind-class palette; v1 keeps importing it). This is a
 * net-new sibling for V2 surfaces built on Glaze's `Badge`/`StatusBadge`
 * components, per plan Key Decision 7 + R8.
 *
 * `BadgeColor` and `StatusBadgeStatus` are imported from Glaze itself
 * (the `@clay-run/glaze` root barrel — its subpath exports like
 * `@clay-run/glaze/badge` need `moduleResolution: bundler`/`node16`, which
 * `tsconfig.tests.json` doesn't set, so the root barrel is the one import
 * path that resolves under every tsconfig this file is type-checked with)
 * rather than redeclared here — a parallel union would drift from Glaze's
 * own type the moment either side changes (terra/no-parallel-schema-and-type).
 *
 * Type-only imports: this module has zero runtime dependency on Glaze/React,
 * so it stays bundle-safe for any frontend or shared context that pulls it
 * in (mini-apps/CLAUDE.md "Frontend/backend split").
 */

/**
 * The subset of Glaze's `BadgeColor` union that are actual categorical hues
 * (as opposed to the `default` / `secondary` / `ghost` structural variants,
 * which aren't part of the 8-hue categorical palette). Verified against
 * `@clay-run/glaze/src/badge.tsx`'s `color` variant map.
 */
export type CategoricalBadgeHue = Extract<
  BadgeColor,
  'red' | 'orange' | 'yellow' | 'green' | 'blue' | 'purple' | 'pink' | 'teal'
>;

/**
 * Stable fallback for unknown/null/undefined subtypes. Arbitrary among the
 * 8 hues (there is no "neutral" categorical hue in the Badge palette to
 * reach for instead) — picked once and fixed so an unrecognized subtype
 * always renders the same color rather than something derived from hash
 * order or map iteration.
 */
const FALLBACK_HUE: CategoricalBadgeHue = 'blue';

/**
 * Real campaign subtype list (~17 values), sourced from v1's
 * `components/subtype-colors.ts` `SUBTYPE_COLORS` map — the closest thing to
 * a canonical enum, since `launcher_campaign_type_config` is populated
 * dynamically from SFDC campaign sync (`campaign-sync.ts`,
 * `launcher-campaign-sync.ts`) rather than from a static seed list.
 *
 * Grouped by what the subtype actually IS (format/purpose), not mechanically
 * carried over from v1's per-subtype fruit-palette assignments:
 *   - blue:   remote/streamed formats (Livestream, Webinar, Virtual)
 *   - green:  skills-building formats (Workshop, Bootcamp)
 *   - purple: Clay-branded community formats (Clay Club, Clay Hosted)
 *   - pink:   hosted meals (Customer Dinner, Founder Dinner, Dinner)
 *   - orange: in-person/on-the-road formats (Roadshow, In-Person)
 *   - red:    marquee/large-scale formats (Flagship, Conference)
 *   - teal:   premium/partnered formats (Executive Event, Partner Co-Branded)
 *   - yellow: sales-demo format (Group Demo)
 */
const SUBTYPE_HUES: Record<string, CategoricalBadgeHue> = {
  Livestream: 'blue',
  Webinar: 'blue',
  Virtual: 'blue',

  Workshop: 'green',
  Bootcamp: 'green',

  'Clay Club': 'purple',
  'Clay Hosted': 'purple',

  'Customer Dinner': 'pink',
  'Founder Dinner': 'pink',
  Dinner: 'pink',

  Roadshow: 'orange',
  'In-Person': 'orange',

  Flagship: 'red',
  Conference: 'red',

  'Executive Event': 'teal',
  'Partner Co-Branded': 'teal',

  'Group Demo': 'yellow',
};

/**
 * Maps a campaign subtype onto one of Glaze Badge's 8 categorical hues.
 * Unknown/null/undefined subtypes get the stable {@link FALLBACK_HUE}.
 */
export function subtypeBadgeColor(subtype: string | null | undefined): CategoricalBadgeHue {
  if (!subtype) return FALLBACK_HUE;
  return SUBTYPE_HUES[subtype] ?? FALLBACK_HUE;
}

/**
 * `Campaign.launcher_status` → Glaze `StatusBadge` status (R8). `StatusBadge`
 * itself documents exactly this producer pattern in its own JSDoc example
 * (`@clay-run/glaze/src/status-badge.tsx`, "Domain-specific wrapper
 * (campaigns)").
 *
 * NARROW ON PURPOSE (Codex adversarial-review fix): the only field this
 * function is ever called with is `Campaign.launcher_status`
 * (`components/types.ts`'s `LauncherStatus = 'draft' | 'launched'`) — a
 * launcher-internal two-value vocabulary, NOT the raw Salesforce
 * `Campaign.Status` field (`'Planning' | 'Active' | ...`, written by
 * `src/services/launcher-campaign-sync.ts`). An earlier version of this map
 * also carried `error`/`waitlisted` keys and was named
 * `campaignStatusBadgeStatus`, inviting exactly that confusion — one call
 * site fed it the raw SFDC status, where the extra keys never matched
 * anything and every value silently fell through to the neutral fallback.
 * Renamed + narrowed to the real vocabulary; the raw SFDC status is never fed
 * through a color mapper (see `QualifyStep.tsx`'s plain, uncolored `Badge`
 * for that field instead).
 */
const LAUNCHER_STATUS_MAP: Record<'launched' | 'draft', StatusBadgeStatus> = {
  launched: 'success',
  draft: 'neutral',
};

/** Stable fallback for an unrecognized launcher status. */
const DEFAULT_STATUS: StatusBadgeStatus = 'neutral';

export function launcherStatusBadgeStatus(status: string | null | undefined): StatusBadgeStatus {
  if (!status) return DEFAULT_STATUS;
  return LAUNCHER_STATUS_MAP[status as 'launched' | 'draft'] ?? DEFAULT_STATUS;
}

// Config-page "kind" and channel badges (Fix 2 of the same adversarial-review
// pass): `PlaybooksConfigPage.tsx` was calling `subtypeBadgeColor()` — whose
// vocabulary is real campaign subtypes — with `'Playbook'`, `'Segmentation'`,
// and a `LauncherChannel` value, none of which are campaign subtypes. Every
// one of those calls silently fell through to `FALLBACK_HUE` (the same blue),
// losing all differentiation. These two small maps give those two domains
// (config-page row kind; sequence channel) their own vocabulary instead.

export type ConfigRowKindLabel = 'Playbook' | 'Segment';

const CONFIG_ROW_KIND_HUES: Record<ConfigRowKindLabel, CategoricalBadgeHue> = {
  Playbook: 'purple',
  Segment: 'teal',
};

/** Hue for the Playbooks/Segmentations section's row-kind badge on the config page. */
export function configRowKindBadgeColor(kind: ConfigRowKindLabel): CategoricalBadgeHue {
  return CONFIG_ROW_KIND_HUES[kind];
}

const CHANNEL_HUES: Record<LauncherChannel, CategoricalBadgeHue> = {
  gong_engage: 'orange',
  inflection: 'yellow',
  slack_copilot: 'blue',
};

/** Hue for a Sequence's channel badge (config page + play-flow). */
export function channelBadgeColor(channel: LauncherChannel): CategoricalBadgeHue {
  return CHANNEL_HUES[channel];
}

/**
 * Indexed-dot color helper over the Glaze `--color-chart-data-0..7` tokens
 * (ported into `web/app-home/src/index.css` alongside this module — see
 * that file's Glaze semantic-token section). Returns a CSS `var(...)`
 * reference, not a Tailwind class, so callers apply it via an inline style
 * (e.g. `style={{ backgroundColor: chartDataDotColor(index) }}`) rather than
 * a `bg-*` utility class — there is no `bg-chart-data-*` utility generated
 * for this token name.
 */
const CHART_DATA_TOKEN_COUNT = 8;

export function chartDataDotColor(index: number): string {
  const bucket = ((index % CHART_DATA_TOKEN_COUNT) + CHART_DATA_TOKEN_COUNT) % CHART_DATA_TOKEN_COUNT;
  return `var(--color-chart-data-${bucket})`;
}
